'use client';

import { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LogoFullColorImgSrc from '../../../assets/images/sendit-logo-full-color.svg';
import LogoFullWhiteImgSrc from '../../../assets/images/sendit-logo-full-white.svg';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import { useCanShowReferralButton } from '../../../js/app/hooks/providers';
import { getHomeUrl } from '../utils/navigation';
import { Button } from './button';
import { ProviderReferralDialog } from './provider-referral-dialog';

// Hook to detect if we're on desktop (lg breakpoint and above)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

interface MobileHeaderProps {
  onMenuClick: () => void;
}

export function MobileHeader({ onMenuClick }: MobileHeaderProps) {
  const location = useLocation();
  const { user, audienceIsStaff: userIsStaff } = useContext(SessionContext);
  const canShowReferralButton = useCanShowReferralButton();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [isReferralDialogOpen, setIsReferralDialogOpen] = useState(false);

  const handleLoginClick = () => {
    const currentPath = location.pathname + location.search;
    const nextParam = encodeURIComponent(currentPath);
    navigate(`/login?next=${nextParam}`);
  };

  // Determine home URL using centralized logic
  const homeUrl = getHomeUrl(user, location.pathname);

  // Don't render on desktop
  if (isDesktop) {
    return null;
  }

  const headerBgClass = userIsStaff ? 'tw:!bg-zinc-200' : 'tw:!bg-brand-black-2';
  const menuButtonClass = userIsStaff
    ? 'tw:text-zinc-900 tw:hover:bg-zinc-950/10'
    : 'tw:text-white tw:hover:bg-zinc-950/10';
  const headerLogoSrc = userIsStaff ? LogoFullColorImgSrc : LogoFullWhiteImgSrc;

  return (
    <header
      className={`tw:sticky tw:top-0 tw:z-30 tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 ${headerBgClass} tw:lg:hidden`}
    >
      {user && (
        <button
          onClick={onMenuClick}
          className={`tw:flex tw:items-center tw:justify-center tw:p-2 tw:rounded-lg tw:transition-colors ${menuButtonClass}`}
          aria-label="Open navigation menu"
        >
          <i className="fas fa-bars tw:text-xl" />
        </button>
      )}
      <Link to={homeUrl} className="tw:flex tw:items-center">
        <img alt="Sendit logo" src={headerLogoSrc.toString()} className="tw:h-8" />
      </Link>
      <div className="tw:flex tw:items-center tw:min-w-10 tw:justify-end">
        {canShowReferralButton ? (
          <Button
            small
            className="tw:!py-2 tw:gap-0 tw:!text-sm"
            color="brand-cta"
            onClick={() => setIsReferralDialogOpen(true)}
          >
            <span className="tw:sm:inline">Refer</span>
            <i className="fas fa-fw fa-dollar-sign tw:ml-1" />
          </Button>
        ) : !user ? (
          <Button small color="brandgreen1" onClick={handleLoginClick}>
            Login
          </Button>
        ) : (
          <div className="tw:w-10" />
        )}
      </div>
      <ProviderReferralDialog
        open={isReferralDialogOpen}
        onClose={() => setIsReferralDialogOpen(false)}
      />
    </header>
  );
}
